import { useUserPreferences } from '@hooks/useUserPreferences';
import { CssBaseline, PaletteMode } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { componentOverrides } from './componentOverrides';
import { darkPalette, lightPalette } from './palette';
import { typography } from './typography';

export const getThemeMode = () => {
  if (typeof window === 'undefined') return 'light';
  const localStorageMode = localStorage.getItem('deckard-theme');
  if (!localStorageMode) {
    localStorage.setItem('deckard-theme', 'light');
    document.getElementsByTagName('body')[0].style.colorScheme = 'normal';
    return 'light';
  }
  document.getElementsByTagName('body')[0].style.colorScheme = localStorageMode;
  return localStorageMode as PaletteMode;
};

export const customTheme = (mode: PaletteMode) =>
  createTheme({
    spacing: 4,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1660,
        xl: 1920,
      },
    },
    palette: {
      mode,
      ...(mode === 'light' ? lightPalette : darkPalette),
    },
    typography,
    components: componentOverrides,
  });

export const ToggleThemeMode = createContext<VoidFunction>(() => {});

export const Theme = ({ children }: PropsWithChildren) => {
  const { themePreference, setTheme } = useUserPreferences();
  const [mode, setMode] = useState<PaletteMode>(getThemeMode());
  const theme = useMemo(() => customTheme(mode), [mode]);

  // Update local state when user preference is loaded
  useEffect(() => {
    if (themePreference && themePreference !== mode) {
      setMode(themePreference);
      localStorage.setItem('deckard-theme', themePreference);
      if (document) {
        document.getElementsByTagName('body')[0].style.colorScheme = themePreference;
      }
    }
  }, [themePreference, mode]);

  const toggleTheme = useCallback(() => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    // Update localStorage immediately for fast UI response
    localStorage.setItem('deckard-theme', newMode);
    setMode(newMode);
    if (document) {
      document.getElementsByTagName('body')[0].style.colorScheme = newMode;
    }
    // Update user preferences for persistence
    void setTheme(newMode);
  }, [setMode, mode, setTheme]);

  return (
    <ToggleThemeMode.Provider value={toggleTheme}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SkeletonTheme
          baseColor={theme.palette.grey['200']}
          highlightColor={theme.palette.grey['50']}
        >
          {children}
        </SkeletonTheme>
      </ThemeProvider>
    </ToggleThemeMode.Provider>
  );
};
